import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import cogoToast from '@successtar/cogo-toast'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { nextBkStep, takeBkPaymentSkip } from './flow'
import { takeSessionId } from '@renderer/utils/sessionIdSource'
import { ensureCameraReadyForStart } from '@renderer/utils/cameras/cameraStartGate'
import BkScreen from './BkScreen'
import { useBkStickerStore } from './stickers'
import { BK_HIT, bkHit } from './layout'

/** Screen 1 — "start". Mints the session, then hands off to payment. */
const Welcome: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, uploadSessionState } = useSessionStateStore()
  const { getPageSettings, config } = useConfigStore()
  const [isStarting, setIsStarting] = useState(false)

  const startSession = async (): Promise<void> => {
    if (isStarting) return
    setIsStarting(true)

    try {
      // The session-start camera gate every other Welcome runs. BK checks the
      // camera nowhere else before payment, so a booth whose DSLR was gone (an
      // app restart that never reconnected it, a camera dropped while idle) took
      // the guest's money and then froze on Filter, the first page that asks for
      // frames: 3 of the 4 sessions that tripped Filter's watchdog on 2026-10-04.
      // It runs before any session state exists, so a blocked start leaves
      // nothing to clean up; View shows the CameraPrompt or Settings.
      if (!(await ensureCameraReadyForStart())) return

      const sessionId = takeSessionId()

      // Sticker placements are store state, not session state, so nothing else
      // clears them — without this the previous guest's stickers would appear
      // on this guest's strip.
      useBkStickerStore.getState().clear()
      // Nor an operator's payment skip from the last guest.
      takeBkPaymentSkip()

      setSessionState({
        id: sessionId,
        projectId,
        mode: 'capture',
        timeline: {
          startTime: new Date(),
          activityLogs: [
            {
              page: 'welcome',
              type: 'session',
              action: 'start',
              details: { mode: 'capture' },
              timestamp: new Date()
            }
          ]
        },
        userPreferences: {
          useGIF: getPageSettings('capture')?.gifEnabled,
          useRecording: getPageSettings('capture')?.recordingEnabled,
          useVideo: getPageSettings('record')?.enabled
        },
        gallery: { id: `${9999999999999 - Date.now()}-${sessionId}` },
        transaction: { discount: 0 }
      })

      uploadSessionState()
      // The Studio's Navigation settings order the next screens: with "Start
      // with payment" off the cut comes first, and payment charges its price.
      navigate(`/${projectId}/${nextBkStep(config?.pages, 'welcome')}`)
    } catch (error) {
      console.error('Error starting session:', error)
      cogoToast.error('Error starting session')
    } finally {
      setIsStarting(false)
    }
  }

  // The Studio's "Tap anywhere to start": the whole plate is the button.
  const tapAnywhere = config?.pages?.welcome?.settings?.tapAnywhereToStart === true

  return (
    <BkScreen plate="welcome.webp">
      {/* The painted "start" lettering, across the ribbon. */}
      <button
        aria-label="Start"
        onClick={startSession}
        disabled={isStarting}
        style={tapAnywhere ? { position: 'absolute', inset: 0 } : bkHit(BK_HIT.welcome.start)}
        className="cursor-pointer bg-transparent"
      />
    </BkScreen>
  )
}

export default Welcome
